// Birthday greeting composer — a self-contained send/schedule modal for a
// single client's birthday EMAIL. It mirrors the dashboard greetings-panel
// composer (HolidayGreetingsPanel) but stands alone so it can open IN PLACE
// from the "Alertas" module without navigating to the dashboard.
//
// Lissette 2026-06-19: clicking a birthday alert in the Alerts module opened
// the composer but bounced the page to the dashboard ("sale para enviar mail
// pero la página se va al dashboard, no se queda en alerta"). Rendering this
// overlay where the click happened keeps her on the Alerts page.
//
// Email-only on purpose — that matches the dashboard's birthday-alert click,
// which also composes an email. The send path is identical to the dashboard's
// (createScheduledSend with source 'birthday_greeting' for a deferred send,
// sendClientEmail for an immediate one), so behavior stays consistent.

import { useState } from 'react';
import { Modal, Input, Textarea, confirmDialog } from './ui.jsx';
import DateTimeField from './DateTimeField.jsx';
import { toast } from './Toast.jsx';
import { AiDraftButton } from './AiDraftButton.jsx';
import { sendClientEmail, createScheduledSend } from '../api.js';
import { localDayKey, defaultGreetingScheduleAt, borrowedEmailNotice, plantillaVivaDelSaludo } from '../greetings.js';

// Belt-and-suspenders fallback if the "et4" birthday template is missing from
// the passed templates. The app seeds emailTemplates from EMAIL_TEMPLATE_DEFAULTS
// (which always includes et4), so this should rarely be hit.
const FALLBACK_BDAY = {
  subject: 'Happy Birthday, {{firstName}}!',
  body: 'Hi {{firstName}},\n\nWishing you a very happy birthday! Thank you for being a valued client.\n\nWarm regards,\n{{agentName}}',
  subjectEs: '¡Feliz Cumpleaños, {{firstName}}!',
  bodyEs: 'Hola {{firstName}},\n\n¡Te deseamos un muy feliz cumpleaños! Gracias por ser un cliente valioso.\n\nUn cordial saludo,\n{{agentName}}',
};

// `person` is the household member whose birthday it is — the primary client,
// their spouse or a dependent. It carries that person's own name/email so the
// greeting is addressed and delivered to them (falling back to the primary's
// email when they have none), and `personKey` + `forDate` are the canonical
// (who, which birthday) pair every screen dedups on — see ../greetings.js.
export default function BirthdayGreetingComposer({ client, person, personKey, forDate, currentUser, lang, emailTemplates, logTeamAction, onClose }) {
  const es = lang === 'es';
  // Who actually receives this. Defaults to the primary client so the older
  // call sites (which pass no `person`) behave exactly as before.
  const recipient = {
    firstName: person?.firstName || client.firstName,
    lastName: person?.lastName || client.lastName,
    email: person?.email || client.email,
    preferredLang: person?.preferredLang || client.preferredLang,
    preferredLanguage: person?.preferredLanguage || client.preferredLanguage,
    rel: person?.rel || '',
    ofName: person?.ofName || '',
  };
  const markerKey = personKey || client.id;
  const bdayDay = forDate || localDayKey();
  // TRUE when the birthday hasn't arrived yet. The composer then pre-fills the
  // ⏰ field with 9:00 AM on the birthday: leaving it empty is what made the
  // "MAÑANA cumple años" cards send a day early the moment they were clicked
  // (ticket 2026-08-14, "varios de esos también se dispararon hoy antes de
  // tiempo"). Clearing the field is still allowed — it just asks first.
  const isFutureBday = bdayDay > localDayKey();
  // Where it actually lands: the birthday person's own address when they have
  // one, otherwise the client record's. Both send paths honour it now — the
  // deferred one always carried its own recipient, and /clients/:id/emails
  // accepts a `to` validated against the household, so a greeting addressed to
  // the wife by name no longer lands in her husband's inbox.
  const deliverTo = () => (recipient.email || client.email) || '';
  // Aviso cuando la dirección NO es de quien cumple sino del titular, porque
  // esa persona no tiene correo propio. El envío es correcto; lo que faltaba
  // era decirlo (../greetings.js explica por qué el texto vive allí).
  const borrowedNote = borrowedEmailNotice({
    ownEmail: person?.email,
    deliverTo: deliverTo(),
    personName: [recipient.firstName, recipient.lastName].filter(Boolean).join(' '),
    ownerName: recipient.ofName,
    rel: recipient.rel,
    lang,
  });
  // Fill per-recipient fields here, but leave {{agentName}}/{{agentEmail}}
  // for the BACKEND to fill from the real sending agent. A seat's browser can
  // hold a stale/empty supervisor and would otherwise sign the greeting with
  // the SEAT's name (currentUser.name) — the send path (client email + the
  // scheduled worker) now resolves the agent server-side and fills these
  // tokens (Lissette 2026-07-14: "todo lo que manda el seat es a nombre del agente").
  const fillTemplate = (tmpl, c) =>
    (tmpl || '')
      .replace(/\{\{firstName\}\}/g, c.firstName || '')
      .replace(/\{\{lastName\}\}/g, c.lastName || '');
  // Honor the client's preferred language first (Lissette has EN + ES clients
  // side by side; the UI language shouldn't dictate what the CLIENT receives).
  const initialMsgLang = (c) => {
    if (c?.preferredLang === 'spanish') return 'es';
    if (c?.preferredLang === 'english') return 'en';
    if (c?.preferredLanguage === 'es') return 'es';
    if (c?.preferredLanguage === 'en') return 'en';
    return es ? 'es' : 'en';
  };
  const tpl = (Array.isArray(emailTemplates) && emailTemplates.find((t) => t.id === 'et4')) || FALLBACK_BDAY;
  const fillBday = (c, ml) => {
    const isEs = ml === 'es';
    return {
      subject: fillTemplate(isEs ? tpl.subjectEs : tpl.subject, c),
      body: fillTemplate(isEs ? tpl.bodyEs : tpl.body, c),
    };
  };

  const startLang = initialMsgLang(recipient);
  const startFill = fillBday(recipient, startLang);
  const [msgLang, setMsgLang] = useState(startLang);
  const [subject, setSubject] = useState(startFill.subject);
  const [body, setBody] = useState(startFill.body);
  const [scheduleAt, setScheduleAt] = useState(() => defaultGreetingScheduleAt(bdayDay));
  const [sending, setSending] = useState(false);
  const [insFocus, setInsFocus] = useState('body'); // which field the field-chips insert into
  const [translating, setTranslating] = useState(false);
  // ES/EN toggle: AI-translate the CURRENT subject + body INTO the chosen
  // language in place (keeps edits). Clicking the active language is a no-op.
  const translateBdayTo = async (to) => {
    if (to === msgLang || translating) return;
    const from = msgLang;
    setTranslating(true);
    try {
      const api = await import('../api.js');
      const tr = async (t) => (t || '').trim() ? ((await api.aiTranslateTemplate({ text: t, fromLang: from, toLang: to })).translated || t) : t;
      const [s, b] = await Promise.all([tr(subject), tr(body)]);
      setSubject(s); setBody(b); setMsgLang(to);
    } catch (e) { toast.error((es ? 'No se pudo traducir: ' : 'Translation failed: ') + (e.message || '')); }
    finally { setTranslating(false); }
  };
  // The subject/body are already personalized when the modal opens, so the
  // chips drop the REAL value (client name, agent name) at the cursor — not a
  // {{token}} that would otherwise be sent literally.
  const insertVal = (val) => {
    const isSubject = insFocus === 'subject';
    const el = document.getElementById(isSubject ? 'bday-subject' : 'bday-body');
    const setter = isSubject ? setSubject : setBody;
    setter((prev) => {
      if (!el || typeof el.selectionStart !== 'number') return prev + val;
      const s = el.selectionStart, e = el.selectionEnd;
      setTimeout(() => { try { el.focus(); el.selectionStart = el.selectionEnd = s + val.length; } catch {} }, 40);
      return prev.slice(0, s) + val + prev.slice(e);
    });
  };

  const togStyle = (which) => ({
    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '.25rem .55rem', borderRadius: 6,
    cursor: sending ? 'wait' : 'pointer',
    border: `1px solid ${msgLang === which ? 'var(--c-cyan,#00d4ff)' : 'var(--c-border,#1a3550)'}`,
    background: msgLang === which ? 'rgba(0,212,255,.1)' : 'var(--c-surface2,#112538)',
    color: msgLang === which ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text2,#7aa3c0)',
    fontSize: '.7rem', fontWeight: 700, fontFamily: 'inherit',
  });

  const confirmSend = async () => {
    if (!subject || !body) return;
    // Sending NOW for a birthday that is still days away means the client gets
    // "¡Feliz cumpleaños!" early. That is almost never the intent, so make it
    // an explicit choice rather than the default the empty ⏰ field used to be.
    if (!scheduleAt && isFutureBday) {
      const ok = await confirmDialog(es
        ? `El cumpleaños es el ${bdayDay}. Sin fecha en "Programar para", el correo sale AHORA — hoy, no el día del cumpleaños. ¿Enviarlo de todas formas?`
        : `The birthday is on ${bdayDay}. With no date in "Schedule for", this email goes out NOW — today, not on their birthday. Send it anyway?`);
      if (!ok) return;
    }
    setSending(true);
    try {
      if (scheduleAt) {
        // Defer: one ScheduledSend row processed by the worker. Source MUST
        // match the backend enum (scheduled-sends.routes.ts): 'birthday_greeting'.
        const runAtIso = new Date(scheduleAt).toISOString();
        // Texto de la plantilla sin tocar → el servidor lo rearma el día del
        // envío con la plantilla de ese día. Editado o traducido → tal cual.
        const original = fillBday(recipient, msgLang);
        const queued = await createScheduledSend({
          clientId: client.id,
          channel: 'email',
          recipient: deliverTo(),
          subject,
          body,
          runAt: runAtIso,
          source: 'birthday_greeting',
          ...plantillaVivaDelSaludo({
            sinTocar: subject === original.subject && body === original.body,
            templateId: tpl.id, tipo: 'bday', lang: msgLang, personKey: markerKey,
          }),
        });
        const at = new Date(scheduleAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
        // The server keeps one greeting per recipient per day, so this may be
        // reporting a row a teammate queued. Say which — "programado" for
        // something that was already handled is how people end up sending
        // twice by hand to "make sure".
        if (queued?._duplicate) {
          toast.info(es
            ? 'Este cliente ya tenía su saludo programado para ese día — no se duplicó.'
            : 'This client already had their greeting scheduled for that day — nothing was duplicated.');
        } else {
          toast.success(es ? `📅 Programado para ${at}` : `📅 Scheduled for ${at}`);
        }
      } else {
        // Plain text → minimal HTML (paragraphs from blank lines, <br> for the rest).
        const html = body.split(/\n\n+/).map((p) => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('');
        // `to` solo cuando NO es la dirección del expediente: así las llamadas
        // de siempre siguen viajando idénticas.
        const to = deliverTo() !== client.email ? deliverTo() : undefined;
        // `source` marca el correo como saludo para que aparezca en el control
        // de repetidos: ese informe lee la cola de envíos, y esto no pasa por
        // la cola. Sin la etiqueta, un saludo mandado a mano quedaba invisible
        // ahí — que es exactamente el caso del ticket original.
        await sendClientEmail(client.id, { subject, html, text: body, to, source: 'birthday_greeting' });
        toast.success(es ? `✅ Enviado a ${deliverTo()}` : `✅ Sent to ${deliverTo()}`);
      }
      // Persist the agency-shared "greeting_sent" marker so the birthday alert
      // flips to "✅ Enviado" for the whole team and in every browser — the
      // same signal the Dashboard's greetings panel writes (../greetings.js
      // owns the key). Without this, Alertas sent the email but left no marker,
      // so the alert kept showing "send" everywhere else (Lissette 2026-07-08);
      // with the WRONG key (bare clientId + today) it left the Inicio card red
      // for spouses and expired at midnight for anything scheduled ahead
      // (ticket 2026-08-14). Fire-and-forget: a marker hiccup must not undo a
      // send that already succeeded.
      try { logTeamAction?.('greeting_sent', markerKey, { kind: 'bday', channel: 'email', scheduled: !!scheduleAt, forDate: bdayDay }); } catch (_) { /* non-fatal */ }
      onClose();
    } catch (err) {
      toast.error('Error: ' + (err?.message || 'unknown'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal title={(es ? 'Vista previa — ' : 'Preview — ') + '📧 Email'} onClose={() => !sending && onClose()} wide>
      {/* Message language toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.75rem' }}>
        <span style={{ fontSize: '.65rem', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
          {es ? 'Idioma del mensaje' : 'Message language'}
        </span>
        <div style={{ display: 'flex', gap: 4 }}>
          <button type="button" onClick={() => translateBdayTo('es')} disabled={sending || translating} style={togStyle('es')} title={es ? 'Traduce asunto + mensaje al español con IA' : 'AI-translate subject + message into Spanish'}>{translating && msgLang === 'es' ? '…' : '🇪🇸 ES'}</button>
          <button type="button" onClick={() => translateBdayTo('en')} disabled={sending || translating} style={togStyle('en')} title={es ? 'Traduce asunto + mensaje al inglés con IA' : 'AI-translate subject + message into English'}>{translating && msgLang === 'en' ? '…' : '🇺🇸 EN'}</button>
        </div>
        <AiDraftButton lang={msgLang === 'es' ? 'es' : 'en'} compact style={{ marginLeft: 'auto' }}
          contextHint={`This is a birthday greeting email from an insurance agent to their client ${[recipient.firstName, recipient.lastName].filter(Boolean).join(' ')}. Warm and celebratory, not salesy. Address the reader with {{firstName}}.`}
          onResult={({ subject: s, body: b }) => { if (s) setSubject(s); if (b) setBody(b); }} />
        {recipient.preferredLang && (
          <span style={{ fontSize: '.62rem', color: 'var(--c-text3,#45688a)', fontStyle: 'italic' }}>
            {es
              ? `(prefiere ${recipient.preferredLang === 'spanish' ? 'español' : 'inglés'})`
              : `(prefers ${recipient.preferredLang === 'spanish' ? 'Spanish' : 'English'})`}
          </span>
        )}
      </div>
      {/* To — the birthday PERSON, which for a spouse/dependent alert is not
          the primary client. It used to always show (and greet) the primary,
          so a spouse's birthday sent "Feliz cumpleaños" to their husband. */}
      <div style={{ marginBottom: '.75rem', padding: '.6rem .8rem', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8 }}>
        <div style={{ fontSize: '.62rem', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 3 }}>{es ? 'Para' : 'To'}</div>
        <div style={{ fontSize: '.85rem', fontWeight: 600, color: 'var(--c-white,#eef6ff)' }}>
          {recipient.firstName} {recipient.lastName}
          <span style={{ fontWeight: 400, color: 'var(--c-text2,#7aa3c0)', marginLeft: 6 }}>&lt;{deliverTo() || '—'}&gt;</span>
        </div>
        {recipient.rel && (
          <div style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)', marginTop: 2 }}>
            {recipient.rel}{recipient.ofName ? ` · ${recipient.ofName}` : ''}
          </div>
        )}
        {borrowedNote && (
          <div style={{
            marginTop: 6, padding: '.45rem .6rem', borderRadius: 6,
            background: 'rgba(255,183,0,.08)', border: '1px solid rgba(255,183,0,.28)',
            fontSize: '.68rem', color: 'var(--c-text,#c8dff0)', lineHeight: 1.45,
          }}>
            <span style={{ color: '#ffb700', fontWeight: 700, marginRight: 4 }}>⚠</span>
            {borrowedNote}
          </div>
        )}
      </div>
      {/* Subject */}
      <div style={{ marginBottom: '.75rem' }}>
        <div style={{ fontSize: '.62rem', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>{es ? 'Asunto' : 'Subject'}</div>
        <Input id="bday-subject" onFocus={() => setInsFocus('subject')} value={subject} onChange={(e) => setSubject(e.target.value)} disabled={sending} />
      </div>
      {/* Message */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ fontSize: '.62rem', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>{es ? 'Mensaje' : 'Message'}</div>
        <Textarea id="bday-body" onFocus={() => setInsFocus('body')} value={body} onChange={(e) => setBody(e.target.value)} rows={8} disabled={sending} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginTop: 6 }}>
          <span style={{ fontSize: '.62rem', color: 'var(--c-text3,#45688a)', fontWeight: 700 }}>{es ? 'Insertar en' : 'Insert into'} {insFocus === 'subject' ? (es ? 'Asunto' : 'Subject') : (es ? 'Mensaje' : 'Message')}:</span>
          {[{ label: es ? 'Nombre' : 'First name', val: recipient.firstName || '' }, { label: es ? 'Apellido' : 'Last name', val: recipient.lastName || '' }, { label: es ? 'Mi nombre' : 'My name', val: currentUser?.name || '' }].filter((ch) => ch.val).map((ch) =>
            <button type="button" key={ch.label} onClick={() => insertVal(ch.val)} disabled={sending}
              style={{ padding: '4px 10px', borderRadius: 8, border: '1px solid rgba(0,212,255,.3)', background: 'rgba(0,212,255,.08)', color: 'var(--c-cyan,#00d4ff)', fontSize: '.72rem', fontWeight: 700, cursor: sending ? 'default' : 'pointer', fontFamily: 'inherit' }}>{ch.label}</button>)}
        </div>
      </div>
      {/* Schedule (optional) */}
      <div style={{ marginBottom: '.75rem', padding: '.6rem .75rem', background: 'var(--c-surface2,#112538)', border: '1px dashed var(--c-border2,#1f3f60)', borderRadius: 9 }}>
        <label style={{ fontSize: '.62rem', color: 'var(--c-text3,#45688a)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 4 }}>
          ⏰ {es ? 'Programar para (opcional)' : 'Schedule for (optional)'}
        </label>
        <DateTimeField value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} disabled={sending} lang={es ? 'es' : 'en'} />
        {scheduleAt ? (
          <div style={{ fontSize: '.62rem', color: 'var(--c-amber,#ffb700)', marginTop: 4 }}>
            {es ? '📅 Se enviará automáticamente en esa fecha/hora.' : '📅 Will send automatically at that date/time.'}
          </div>
        ) : isFutureBday && (
          <div style={{ fontSize: '.62rem', color: 'var(--c-red,#ff3d6b)', marginTop: 4 }}>
            {es ? `⚠️ Sin fecha, el correo sale HOY — el cumpleaños es el ${bdayDay}.` : `⚠️ With no date the email goes out TODAY — the birthday is ${bdayDay}.`}
          </div>
        )}
      </div>
      <div style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)', marginBottom: '.75rem', fontStyle: 'italic' }}>
        {es
          ? 'Se enviará desde tu cuenta de Deskte (Gmail/Outlook conectada) y aparecerá en la pestaña Emails del cliente.'
          : 'Will send from your connected Deskte mailbox (Gmail/Outlook) and appear in this client\'s Emails tab.'}
      </div>
      <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end' }}>
        <button type="button" onClick={() => onClose()} disabled={sending} className="dkt-btn-g">{es ? 'Cancelar' : 'Cancel'}</button>
        <button
          type="button"
          onClick={confirmSend}
          disabled={sending || !subject || !body || !deliverTo()}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'linear-gradient(135deg,#059669,#10b981)', border: 'none', color: '#fff', fontSize: '.82rem', fontWeight: 800, padding: '.55rem 1.4rem', borderRadius: 9, cursor: sending ? 'wait' : 'pointer', fontFamily: 'inherit', opacity: sending ? 0.7 : 1 }}
        >
          {sending
            ? (scheduleAt ? (es ? 'Programando…' : 'Scheduling…') : (es ? 'Enviando…' : 'Sending…'))
            : (scheduleAt ? (es ? '📅 Programar envío' : '📅 Schedule send') : (es ? '✉️ Enviar por Deskte' : '✉️ Send via Deskte'))}
        </button>
      </div>
    </Modal>
  );
}
